import map from 'lodash/map';
import toPairsIn from 'lodash/toPairsIn';
import pickBy from 'lodash/pickBy';
import isNumber from 'lodash/isNumber';
import forEach from 'lodash/forEach';
import compact from 'lodash/compact';
import values from 'lodash/values';

export type Aspect = 'happiness' | 'environment' | 'qualityOfLife';

export type Points = number;

/**
 * A score that is composed of {@link Points} from several {@link Aspect}s. Points can be negative or undefined for particular aspect
 */
export type Score = {
	[K in Aspect]?: Points;
};

/**
 * A total score summed from a set of {@link Score}s.
 * The points are set for every {@link Aspect}
 */
export type TotalScore = Required<Score>;

/**
 * Scores for a set of personas.
 * Here, currently only the points for {@link Score.happiness} should change, but in the future it could also be points for other aspects.
 */
export type PersonaScores = {
	[personaName: string]: Score | undefined;
};

export const EMPTY_TOTAL_SCORE: TotalScore = {
	environment: 0,
	happiness: 0,
	qualityOfLife: 0
};

export const isEqualTotalScore = (
	score1: TotalScore,
	score2: TotalScore
): boolean => {
	return (
		score1.environment === score2.environment &&
		score1.happiness === score2.happiness &&
		score1.qualityOfLife === score2.qualityOfLife
	);
};

export const sumScores = (pointsList: Score[]): TotalScore => {
	const sum: TotalScore = {
		environment: 0,
		happiness: 0,
		qualityOfLife: 0
	};
	forEach(pointsList, (points) => {
		sum.environment += points.environment ?? 0;
		sum.happiness += points.happiness ?? 0;
		sum.qualityOfLife += points.qualityOfLife ?? 0;
	});
	return sum;
};

export const sumPoints = (score: Score | TotalScore): Points => {
	return (
		(score.environment ?? 0) +
		(score.happiness ?? 0) +
		(score.qualityOfLife ?? 0)
	);
};

export const hasPoints = (score: Score | TotalScore): boolean => {
	return sumPoints(score) !== 0;
};

export const sumPersonaScores = (personaScores: PersonaScores): Score => {
	const pointsList = compact(values(personaScores));
	return sumScores(pointsList);
};

export const hasScore = (
	lastPersonaScores: PersonaScores,
	personaName: string
): boolean => {
	return lastPersonaScores[personaName] !== undefined;
};

export const mapScorePoints = <T>(
	score: Score | TotalScore,
	mapper: (aspect: Aspect, points: Points) => T
): T[] => {
	return map(toPairsIn(pickBy(score, isNumber)), (pair) =>
		mapper(pair[0] as Aspect, pair[1])
	);
};

export const getAspectTranslationKey = (aspect: Aspect): string => {
	switch (aspect) {
		case 'environment':
			return 'environment';
		case 'happiness':
			return 'happiness';
		case 'qualityOfLife':
			return 'quality_of_life';
	}
};
